import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import type { BusinessInvitation, BusinessInvitationState } from "lens-platform-sdk";
import { LensSDKException } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";

/**
 * invitations with role="Administrator" to the business
 */
export type BusinessAdminInvitation = BusinessInvitation & {
  role: "Administrator";
};

/**
 * Get user's business entity (Lens Business IDs)'s invitations by business entity ID.
 */
function useGetBusinessInvitations(businessId?: string, businessInvitationState?: BusinessInvitationState) {
  const lensPlatformClient = useLensPlatformClient();

  const [businessInvitations, setBusinessInvitations] = useState<BusinessInvitation[]>([]);

  const [error, setError] = useState<LensSDKException | null>(null);
  const [loading, setLoading] = useState(false);

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const getBusinessInvitations = useCallback(async () => {
    if (businessId) {
      try {
        if (mounted.current) {
          setLoading(true);
        }
        const response = await lensPlatformClient.business.getInvitations(businessId);

        if (mounted.current) {
          setBusinessInvitations(response);
          setError(null);
        }
      } catch (exception) {
        if (mounted.current && exception instanceof LensSDKException) {
          setError(exception);
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }
    }
  }, [lensPlatformClient.business, businessId]);

  useEffect(() => {
    // Load businesses on mount
    getBusinessInvitations();
  }, [getBusinessInvitations]);

  const filteredBusinessInvitations = useMemo(
    () =>
      businessInvitationState
        ? businessInvitations.filter((invitation) => invitation.state === businessInvitationState)
        : businessInvitations,
    [businessInvitationState, businessInvitations],
  );

  return {
    loading,
    error,
    businessInvitations: filteredBusinessInvitations,
    reload: getBusinessInvitations,
  };
}

export { useGetBusinessInvitations };
